// The parts of a whole and a ranked list's columns, from a stream, for the
// breakdowns (breakdown.tsx). Pure, so tests can check them without a browser.

import type { Stream, StreamItem } from "../../shared/types";

/** "58%", "6%", "0.4%", "<0.1%": a share of the whole, in whole percents, with a decimal only under one. */
export function shareText(part: number, whole: number): string {
  if (!(whole > 0)) return "";
  const p = (part / whole) * 100;
  if (p > 0 && p < 0.1) return "<0.1%";
  return `${p >= 0.95 || p === 0 ? Math.round(p) : p.toFixed(1)}%`;
}

export interface Part {
  label: string;
  v: number;
  /** its color: one of the theme's five chart colors, or none for everything else added up */
  tone: number | null;
}

/** The parts of a whole, biggest first: up to five in the theme's colors (fewer when most is less), and the rest added up as Other. Only real counts, above zero. */
export function partsOf(items: StreamItem[], most = 6): { parts: Part[]; total: number } {
  const real = items.filter((i) => typeof i.value === "number" && Number.isFinite(i.value) && i.value > 0).sort((a, b) => (b.value as number) - (a.value as number));
  const total = real.reduce((n, i) => n + (i.value as number), 0);
  const top = real.length > Math.min(6, most) ? real.slice(0, Math.max(1, Math.min(5, most - 1))) : real;
  const parts: Part[] = top.map((i, k) => ({ label: i.label, v: i.value as number, tone: k }));
  if (real.length > top.length) parts.push({ label: `Other (${real.length - top.length})`, v: real.slice(top.length).reduce((n, i) => n + (i.value as number), 0), tone: null });
  return { parts, total };
}

/** A list whose rows carry more numbers than its value: its first column names each item, its second is the value. */
export function rankedColumns(s: Pick<Stream, "items" | "rows" | "columns">): string[] | null {
  const items = s.items ?? [];
  const rows = s.rows ?? [];
  const cols = s.columns ?? [];
  if (!items.length || rows.length !== items.length || cols.length < 3) return null;
  return items.every((it, i) => String(rows[i][cols[0]]) === it.label) ? cols : null;
}

